import type { InteriorPanelDirection } from "../domain/finishes/types";
import type {
	RectRoofGeometry,
	RectWallName,
} from "../domain/roof/rectRoofGeometry";
import { ENGINE_INTERIOR_BATTEN_CC_M as BATTEN_CC } from "./constants";
import { defaultStudLinePositions } from "./facadeQuantities";

export interface InteriorLiningQuantities {
	/**
	 * Inv. spikläkt / glespanel, in lpm, for the SELECTED panel direction.
	 *
	 * Korsläkt: the battens must cross the boards they carry. Standing panel
	 * (the default) therefore gets LYING rows — `rows up each wall × that
	 * wall's length` — while lying panel gets STANDING runs, one per stud line
	 * and each as tall as the wall is there. Those are genuinely different
	 * numbers, which is why `panelDirection` is not a cosmetic choice.
	 */
	lmInteriorBattens: number;
	/**
	 * The same quantity under the OTHER direction. Carried so the builder can
	 * price the direction swatch without re-running the engine — see
	 * `FinishPricingBasis`.
	 */
	lmInteriorBattensAlternate: number;
	/** Rows generated on a wall of the given roof-aware max height. */
	rowsForWall: (wall: "front" | "back" | "left" | "right") => number;
}

/**
 * BOM quantities for the interior lining layers that `buildInteriorWallLayers`
 * renders. One definition shared by the square and two-room engines so the
 * purchase list always mirrors the 3D build-up.
 *
 * `renderInteriorLyingBattenStrips` places a bottom row at the syll, then a row
 * every `ENGINE_INTERIOR_BATTEN_CC_M` up to the wall's MAX height (gable ridge /
 * mono high eave), plus a pinned top row — so `ceil(maxHeight / cc) + 1` rows.
 * Rows are counted over the full wall rectangle while the renderer clips them
 * to the roof profile, which slightly over-buys on gable triangles: the same
 * safe direction the facade korsläkt row math takes.
 *
 * `dividerSpans` carries any interior partition faces that also get glespanel
 * (the two-room delningsvägg is panelled — and therefore battened — on both
 * sides); each entry is one face's span length and height.
 */
export function calculateInteriorLiningQuantities(input: {
	W: number;
	L: number;
	roofGeometry: RectRoofGeometry;
	dividerSpans?: Array<{ lengthM: number; heightM: number }>;
	/** Defaults to `"standing"` — the pre-issue-134 behaviour, unchanged. */
	panelDirection?: InteriorPanelDirection;
	/**
	 * Stud axes per wall, used only by the STANDING-batten branch (one run per
	 * unique axis, exactly like the facade's ströläkt). Callers that never
	 * offer lying panel can omit it; the standing branch then falls back to the
	 * plain c/c grid the framing generator would lay.
	 */
	studLinesByWall?: Record<RectWallName, number[]>;
}): InteriorLiningQuantities {
	const { W, L, roofGeometry } = input;
	const rowsForWall = (wall: "front" | "back" | "left" | "right") =>
		Math.ceil(roofGeometry.getMaxWallHeight(wall) / BATTEN_CC) + 1;

	const wallSpans: Array<{
		wall: "front" | "back" | "left" | "right";
		length: number;
	}> = [
		{ wall: "front", length: W },
		{ wall: "back", length: W },
		{ wall: "left", length: L },
		{ wall: "right", length: L },
	];

	const dividerLm = (input.dividerSpans ?? []).reduce(
		(sum, span) =>
			sum + span.lengthM * (Math.ceil(span.heightM / BATTEN_CC) + 1),
		0,
	);

	// LYING battens (carry a STANDING panel): rows × wall length.
	const lyingLm =
		wallSpans.reduce(
			(sum, { wall, length }) => sum + length * rowsForWall(wall),
			0,
		) + dividerLm;

	// STANDING battens (carry a LYING panel): one run per stud axis, each as
	// tall as the wall is at that axis — the roof profile, not a flat height.
	// Same shape as `calculateVentilatedFacadeQuantities`'s `lmStroToLakt`,
	// because it is the same layer on the other face of the same studs.
	const standingLm =
		wallSpans.reduce((sum, { wall }) => {
			const positions = [
				...new Set(
					input.studLinesByWall?.[wall] ??
						defaultStudLinePositions(
							wall === "front" || wall === "back" ? W : L,
						),
				),
			];
			return (
				sum +
				positions.reduce(
					(wallSum, localX) =>
						wallSum + roofGeometry.getWallHeightAt(wall, localX),
					0,
				)
			);
		}, 0) +
		// A divider face battened vertically buys runs at the same c/c the wall
		// studs use, each the full face height.
		(input.dividerSpans ?? []).reduce(
			(sum, span) =>
				sum + defaultStudLinePositions(span.lengthM).length * span.heightM,
			0,
		);

	const isLyingPanel = input.panelDirection === "lying";

	return {
		lmInteriorBattens: Math.ceil(isLyingPanel ? standingLm : lyingLm),
		lmInteriorBattensAlternate: Math.ceil(isLyingPanel ? lyingLm : standingLm),
		rowsForWall,
	};
}
